import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import API_BASE_URL, { MEDIA_BASE_URL } from '../config';
import {
  FaShieldAlt, FaAward, FaLightbulb, FaHeadset,
  FaArrowRight, FaBuilding, FaGlobe, FaUsers,
  FaChartLine, FaStar, FaCheckCircle,
  FaQuoteRight, FaBullseye, FaEye, FaUserTie,
  FaTimes,
} from 'react-icons/fa';
import './About.css';

const getImageUrl = (imagePath, fallbackUrl) => {
  if (!imagePath) return fallbackUrl;
  if (imagePath.startsWith('http')) return imagePath;
  return `${MEDIA_BASE_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
};

const About = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeJourney, setActiveJourney] = useState(null);

  const { ref: statsRef, inView: statsVisible } = useInView({ threshold: 0.3, triggerOnce: true });
  const { ref: storyRef, inView: storyVisible } = useInView({ threshold: 0.2, triggerOnce: true });
  const { ref: valuesRef, inView: valuesVisible } = useInView({ threshold: 0.2, triggerOnce: true });
  const { ref: leadersRef, inView: leadersVisible } = useInView({ threshold: 0.2, triggerOnce: true });

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/public/landing/`);
        setPageData(res.data || null);
      } catch (err) {
        console.error('Error fetching about page data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAboutData();
  }, []);

  useEffect(() => {
    document.body.style.overflow = activeJourney ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [activeJourney]);

  const coreValues = [
    { icon: <FaShieldAlt />, title: 'Integrity & Transparency', desc: 'Honest practices and open communication form the foundation of our stakeholder relationships.', num: '01' },
    { icon: <FaAward />, title: 'Quality Excellence', desc: 'Delivering the highest quality standards in everything we do, every single time.', num: '02' },
    { icon: <FaLightbulb />, title: 'Innovation & Growth', desc: 'Creating smarter solutions today for a better, brighter, and more sustainable tomorrow.', num: '03' },
    { icon: <FaHeadset />, title: 'Customer First', desc: 'Your satisfaction and long-term success remain our top priority at all times.', num: '04' },
  ];

  const aboutTitle = pageData?.about?.page_title || 'Building Trust. Creating Value.';
  const aboutDesc = pageData?.about?.description || 'Sarwa Group is a diversified business conglomerate operating across multiple industries.';
  const vision = pageData?.about?.vision || 'To build businesses that create positive change in society.';
  const mission = pageData?.about?.mission || 'Transparency, honesty, and quality are the true keys to long-term success.';
  const ownerName = pageData?.about?.owner_name || pageData?.about?.ownerName || 'Mr. Rajesh Mehta';

  const rawAboutImg = pageData?.about?.about_image || pageData?.about?.aboutImage;
  const corporateImage = getImageUrl(rawAboutImg, 'https://images.unsplash.com/photo-1556761175-5973dc0f32d7?q=80&w=1632&auto=format&fit=crop');
  const heroImage = 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2069&auto=format&fit=crop';

  // DYNAMIC JOURNEY FROM BACKEND (with fallback)
  const journey = pageData?.journey?.length > 0
    ? pageData.journey
    : [
      { id: 1, year: '2021', title: 'Foundation', short_desc: 'Sarwa Group established with a vision of excellence.', full_desc: 'Sarwa Group was founded with a clear vision to create a diversified business conglomerate that operates with integrity, transparency, and an unwavering commitment to excellence. What started as a small venture has now grown into a multi-sector enterprise touching lives across education, healthcare, and infrastructure.', icon: '🏗️' },
      { id: 2, year: '2022', title: 'Expansion', short_desc: 'Entered Education and Medical verticals.', full_desc: 'Recognizing the growing need for quality education and healthcare in Central India, Sarwa Group expanded into these critical sectors. We launched smart classroom initiatives and partnered with leading medical institutions to bring world-class services to underserved communities.', icon: '🚀' },
      { id: 3, year: '2023', title: 'Growth', short_desc: 'Launched Construction Materials division.', full_desc: 'Our Construction Materials division was established to address the growing demand for premium, eco-friendly building materials. With our fly ash brick manufacturing plant, we are contributing to sustainable infrastructure development while reducing environmental impact.', icon: '📈' },
      { id: 4, year: '2024', title: 'Excellence', short_desc: 'Achieved industry recognition and partnerships.', full_desc: '2024 marked a milestone year for Sarwa Group as we received multiple industry recognitions and forged strategic partnerships with leading national and international organizations. Our commitment to quality and innovation has positioned us as a trusted name across all our verticals.', icon: '🏆' },
    ];

  const stats = useMemo(() => {
    const yearEst = pageData?.home?.year_established !== undefined && pageData?.home?.year_established !== null ? Number(pageData.home.year_established) : 2021;
    const verticalsCount = Array.isArray(pageData?.services) ? pageData.services.length : 0;
    const portfolioCount = Array.isArray(pageData?.portfolio) ? pageData.portfolio.length : 0;
    const serviceProjectsCount = Array.isArray(pageData?.service_projects) ? pageData.service_projects.length : 0;
    const totalProjects = portfolioCount + serviceProjectsCount;
    const teamCount = Array.isArray(pageData?.team) ? pageData.team.length : 0;
    return [
      { icon: <FaBuilding />, num: yearEst, suffix: '', label: 'Year Established' },
      { icon: <FaGlobe />, num: verticalsCount, suffix: '+', label: 'Business Verticals' },
      { icon: <FaChartLine />, num: totalProjects, suffix: '+', label: 'Projects Delivered' },
      { icon: <FaUsers />, num: teamCount, suffix: '+', label: 'Team Members' },
      { icon: <FaStar />, num: 100, suffix: '%', label: 'Client Satisfaction' },
    ];
  }, [pageData]);

  const leaders = Array.isArray(pageData?.team) && pageData.team.length > 0
    ? pageData.team.map((m) => ({
      name: m.full_name || m.name || 'Team Member',
      role: m.role || m.designation || 'Team',
      desc: m.experience ? `Experience: ${m.experience}${m.education ? ` | ${m.education}` : ''}${m.location ? ` | ${m.location}` : ''}` : m.bio || 'Driving excellence with strong leadership.',
      image: m.avatar_icon || m.image || null,
      initial: (m.full_name || m.name || 'T').charAt(0),
    }))
    : [
      { name: 'Mr. Rajesh Mehta', role: 'Founder & Managing Director', desc: 'Visionary leader with 20+ years of experience.', image: null, initial: 'R' },
      { name: 'Ms. Anjali Gupta', role: 'Co-Founder & Director', desc: 'Driving innovation and excellence across all verticals.', image: null, initial: 'A' },
    ];

  if (loading) {
    return (
      <div className="sg-loader">
        <div className="sg-loader-inner">
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <div className="sg-loader-ring"></div>
          <span className="sg-loader-text">SARWA GROUP</span>
        </div>
      </div>
    );
  }

  return (
    <main className="sg-about-page">
      {/* HERO */}
      <section className="sg-about-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="sg-about-hero-overlay"></div>
        <div className="sg-hero-decor">
          <div className="sg-hero-circle sg-hero-circle-1"></div>
          <div className="sg-hero-circle sg-hero-circle-2"></div>
        </div>
        <div className="container sg-about-hero-content">
          <div className="sg-breadcrumb">
            <Link to="/">Home</Link>
            <span className="sg-crumb-sep">/</span>
            <span className="sg-crumb-current">About Us</span>
          </div>
          <div className="sg-hero-text-center">
            <div className="sg-hero-badge">
              <span className="sg-badge-dot"></span>
              <span>COMPANY OVERVIEW</span>
              <span className="sg-badge-dot"></span>
            </div>
            <h1 className="sg-about-hero-title">
              {aboutTitle.includes('.') ? (
                <>{aboutTitle.split('.')[0]}.<br /><span className="sg-gold-word">{aboutTitle.split('.').slice(1).join('.').trim() || 'Creating Value.'}</span></>
              ) : aboutTitle}
            </h1>
            <div className="sg-hero-separator">
              <span className="sg-sep-line"></span>
              <span className="sg-sep-diamond"></span>
              <span className="sg-sep-line"></span>
            </div>
            <p className="sg-about-hero-desc">{aboutDesc}</p>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className={`sg-story-section section-padding ${storyVisible ? 'sg-visible' : ''}`} ref={storyRef}>
        <div className="container sg-story-grid">
          <div className="sg-story-visual">
            <div className="sg-story-img-wrapper">
              <div className="sg-story-img-frame"></div>
              <img src={corporateImage} alt="Sarwa Group Corporate" className="sg-story-img" />
              <div className="sg-story-floating-card">
                <div className="sg-sfc-icon"><FaUserTie /></div>
                <div>
                  <strong>{ownerName}</strong>
                  <span>Founder &amp; MD</span>
                </div>
              </div>
              <div className="sg-story-dot-pattern"></div>
            </div>
          </div>
          <div className="sg-story-content">
            <span className="sg-section-tag">OUR PHILOSOPHY</span>
            <h2 className="sg-section-heading">Driven by <span className="sg-gold-word">Vision.</span><br />Defined by <span className="sg-gold-word">Values.</span></h2>
            <div className="sg-heading-line"></div>
            <div className="sg-vm-block">
              <div className="sg-vm-item">
                <div className="sg-vm-icon"><FaEye /></div>
                <div className="sg-vm-text"><h4>Our Vision</h4><p>{vision}</p></div>
              </div>
              <div className="sg-vm-divider"></div>
              <div className="sg-vm-item">
                <div className="sg-vm-icon"><FaBullseye /></div>
                <div className="sg-vm-text"><h4>Our Mission</h4><p>{mission}</p></div>
              </div>
            </div>
            <div className="sg-highlights">
              <div className="sg-highlight-item"><FaCheckCircle /><span>Multi-Sector Excellence</span></div>
              <div className="sg-highlight-item"><FaCheckCircle /><span>Ethical Business Practices</span></div>
              <div className="sg-highlight-item"><FaCheckCircle /><span>Sustainable Growth Model</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats-strip" ref={statsRef}>
        <div className="container stats-grid-5">
          {stats.map((s, i) => (
            <div key={i} className="stat-box">
              <div className="stat-icon-mini">{s.icon}</div>
              <h3 className="stat-number">
                {statsVisible ? <CountUp end={s.num} duration={2.5} separator={s.label.includes('YEAR') ? "" : ","} /> : 0}
                <span className="stat-suffix">{s.suffix}</span>
              </h3>
              <span className="stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* OUR JOURNEY / TIMELINE (PREMIUM DYNAMIC DESIGN) */}
      <section className="sg-journey section-padding">
        <div className="container">
          <div className="sg-section-header-center">
            <span className="sg-section-tag">OUR JOURNEY</span>
            <h2 className="sg-section-heading">
              Milestones of <span className="sg-gold-word">Excellence</span>
            </h2>
            <div className="sg-journey-header-line">
              <span className="sg-jhl-line"></span>
              <span className="sg-jhl-diamond"></span>
              <span className="sg-jhl-line"></span>
            </div>
            <p className="sg-section-subtext">
              From humble beginnings to a diversified conglomerate — every milestone
              tells a story of vision, perseverance, and relentless pursuit of excellence.
            </p>
          </div>

          {journey.length > 0 ? (
            <div className="sg-timeline-premium">
              {/* Central Line */}
              <div className="sg-tl-central-line">
                <div className="sg-tl-line-glow"></div>
              </div>

              {/* Timeline Year Navigation Dots */}
              <div className="sg-tl-year-nav">
                {journey.map((j, i) => (
                  <button
                    key={j.id || i}
                    className="sg-tl-year-dot"
                    onClick={() => {
                      const el = document.getElementById(`journey-${j.id || i}`);
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    title={`${j.year} - ${j.title}`}
                  >
                    <span className="sg-tl-yd-year">{j.year}</span>
                  </button>
                ))}
              </div>

              {/* Timeline Items */}
              {journey.map((j, i) => (
                <div
                  key={j.id || i}
                  id={`journey-${j.id || i}`}
                  className={`sg-tl-item ${i % 2 === 0 ? 'sg-tl-left' : 'sg-tl-right'}`}
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  {/* Connector Line */}
                  <div className="sg-tl-connector">
                    <div className="sg-tl-connector-line"></div>
                  </div>

                  {/* Center Node */}
                  <div className="sg-tl-node">
                    <div className="sg-tl-node-ring"></div>
                    <div className="sg-tl-node-core">
                      <span className="sg-tl-node-icon">{j.icon || '🏆'}</span>
                    </div>
                    <div className="sg-tl-node-pulse"></div>
                  </div>

                  {/* Content Card */}
                  <div
                    className="sg-tl-card"
                    onClick={() => setActiveJourney(j)}
                  >
                    {/* Card Top Accent */}
                    <div className="sg-tl-card-accent"></div>

                    {/* Card Number Badge */}
                    <span className="sg-tl-card-num">
                      {String(i + 1).padStart(2, '0')}
                    </span>

                    {/* Year Badge */}
                    <div className="sg-tl-card-year-badge">
                      <span className="sg-tl-cyb-icon">{j.icon || '🏆'}</span>
                      <span className="sg-tl-cyb-year">{j.year}</span>
                    </div>

                    {/* Card Content */}
                    <h4 className="sg-tl-card-title">{j.title}</h4>
                    <p className="sg-tl-card-desc">{j.short_desc}</p>

                    {/* Read More */}
                    <div className="sg-tl-card-footer">
                      <span className="sg-tl-read-more">
                        Read Full Story
                        <FaArrowRight className="sg-tl-arrow" />
                      </span>
                      <div className="sg-tl-card-dots">
                        <span></span><span></span><span></span>
                      </div>
                    </div>

                    {/* Hover Glow */}
                    <div className="sg-tl-card-glow"></div>
                  </div>
                </div>
              ))}

              {/* Timeline End Cap */}
              <div className="sg-tl-end-cap">
                <div className="sg-tl-end-icon">
                  <FaStar />
                </div>
                <span>The Journey Continues...</span>
              </div>
            </div>
          ) : (
            <div className="sg-journey-empty">
              <div className="sg-journey-empty-icon">🏗️</div>
              <h3>Journey Milestones Coming Soon</h3>
              <p>We're documenting our incredible journey. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CORE VALUES */}
      <section className={`sg-values-section section-padding ${valuesVisible ? 'sg-visible' : ''}`} ref={valuesRef}>
        <div className="sg-values-bg-pattern"></div>
        <div className="container">
          <div className="sg-section-header-center">
            <span className="sg-section-tag">FOUNDATION</span>
            <h2 className="sg-section-heading">Our Core Values</h2>
            <p className="sg-section-subtext">The principles that guide every decision, every project, and every relationship at Sarwa Group.</p>
          </div>
          <div className="sg-values-grid">
            {coreValues.map((v, i) => (
              <div key={i} className="sg-value-card" style={{ animationDelay: `${i * 0.1}s` }}>
                <span className="sg-value-num">{v.num}</span>
                <div className="sg-value-icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
                <div className="sg-value-accent"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className={`sg-leadership section-padding ${leadersVisible ? 'sg-visible' : ''}`} ref={leadersRef}>
        <div className="container">
          <div className="sg-section-header-center">
            <span className="sg-section-tag">EXECUTIVE BOARD</span>
            <h2 className="sg-section-heading">Leadership Team</h2>
            <p className="sg-section-subtext">Meet the visionaries and industry leaders driving Sarwa Group towards a brighter future.</p>
          </div>
          <div className="sg-leaders-grid">
            {leaders.map((l, i) => (
              <div key={i} className="sg-leader-card" style={{ animationDelay: `${i * 0.15}s` }}>
                <div className="sg-leader-visual">
                  <div className="sg-leader-avatar">
                    {l.image && String(l.image).startsWith('http') ? (
                      <img src={l.image} alt={l.name} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="sg-leader-initial">{l.initial}</span>
                    )}
                  </div>
                  <div className="sg-leader-quote-icon"><FaQuoteRight /></div>
                </div>
                <div className="sg-leader-content">
                  <h3>{l.name}</h3>
                  <span className="sg-leader-role">{l.role}</span>
                  <div className="sg-leader-line"></div>
                  <p>{l.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY POPUP MODAL */}
      {activeJourney && (
        <div className="sg-journey-modal-overlay" onClick={() => setActiveJourney(null)}>
          <div className="sg-journey-modal" onClick={(e) => e.stopPropagation()}>
            <button className="sg-journey-modal-close" onClick={() => setActiveJourney(null)}>
              <FaTimes />
            </button>
            <div className="sg-journey-modal-header">
              <span className="sg-journey-modal-icon">{activeJourney.icon || '🏆'}</span>
              <span className="sg-journey-modal-year">{activeJourney.year}</span>
            </div>
            <h2 className="sg-journey-modal-title">{activeJourney.title}</h2>
            <div className="sg-journey-modal-divider"></div>
            <div className="sg-journey-modal-body">
              <p>{activeJourney.full_desc || activeJourney.short_desc}</p>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default About;